import {
  AuthStage,
  AuthState,
  Keys,
  LogoutReason,
  UnknownAppStage,
  appAuthAtom,
  isAxiosError,
  mifidMessageAtom,
} from '@eih-netbroker/common';
import { useAtom } from 'jotai';
import { useCallback, useMemo, useRef } from 'react';
import { useDialog } from '../../../context/DialogContext';
import { FirstLoginDisclaimerModal } from '../../../modules/home/partials/FirstLoginDisclaimerModal';
import { MifidMessageModal } from '../../../modules/home/partials/MifidMessageModal';
import { useWatchList } from '../../../modules/watchlist/services/useWatchList';
import { useLogger } from '../../utils/useLogger';
import { sseManager } from '../sse/sseManager';
import { useConsents } from '../useConsents';
import { useSession } from '../useSession';
import { useUserSettings } from '../user-settings/useUserSettings';
import { useAuthEvents } from './useAuthEvents';
import { useLogout } from './useLogout';

const AppAuthSteps = [
  AuthStage.Step1_LoadUserSettings,
  AuthStage.Step2_LoadSessionInit,
  AuthStage.Step3_LoadConsents,
  AuthStage.Step4_LoadAppData,
];

export const useAuthorizationFlow = () => {
  const logger = useLogger('useAuthorizationFlow');
  const { loadUserSettings: _loadUserSettings } = useUserSettings();
  const [appAuth, setAppAuth] = useAtom(appAuthAtom);
  const initInProgress = useRef<boolean>(false);
  const logoutInProgress = useRef<boolean>(false);
  const authEvents = useAuthEvents();
  const { sessionInit } = useSession();
  const { loadWatchListGroups } = useWatchList();
  const { getConsents } = useConsents();
  const { openDialog } = useDialog();
  const { logout } = useLogout();
  const [mifidMessage] = useAtom(mifidMessageAtom);

  const onDisplayMifidMessage = useCallback(async () => {
    await openDialog((close: () => void) => (
      <MifidMessageModal
        onCancel={close}
        text={mifidMessage}
      />
    ));
  }, [mifidMessage, openDialog]);

  const loadUserSettings = async () => await _loadUserSettings();

  const loadSessionInit = async () => await sessionInit();

  const loadConsents = async () => {
    const firstLoginConsent = getConsents().find(consent => consent.consentId === Keys.Consents.FirstLogin);

    if (!firstLoginConsent) {
      logout(LogoutReason.ConsentFirstLoginMissing);
      return false;
    }
    if (!firstLoginConsent.accepted) {
      const res = await openDialog((close: (res: boolean | undefined) => void) => (
        <FirstLoginDisclaimerModal onCancel={close} />
      ));
      if (res === undefined) {
        logout(LogoutReason.ConsentFirstLoginRejected);
        return false;
      } else if (res === false) {
        logout(LogoutReason.ErrorWhileSavingConsent);
        return false;
      }
    }

    if (mifidMessage.length > 0) {
      await onDisplayMifidMessage();
    }
  };

  const loadAppData = async () => {
    loadWatchListGroups();
  };

  const AppAuthStepFunctions: Record<AuthStage, () => Promise<boolean | unknown | void>> = {
    [AuthStage.Step1_LoadUserSettings]: loadUserSettings,
    [AuthStage.Step2_LoadSessionInit]: loadSessionInit,
    [AuthStage.Step3_LoadConsents]: loadConsents,
    [AuthStage.Step4_LoadAppData]: loadAppData,
    [AuthStage.OtherInstanceLoginInProgress]: async () => {},
    [AuthStage.Step_SetPin]: async () => {},
    [AuthStage.Step_SetBiometric]: async () => {},
  };

  /**
   * Internal login method
   * @param jwtToken
   * @param idToken
   * @param props
   */
  const loginInternal = useCallback(
    async (
      jwtToken: string,
      idToken: string,
      props?: { fromOtherInstance?: boolean; refreshToken?: boolean },
    ): Promise<void> => {
      try {
        logger.info(`Login internal`, props || {});
        await authEvents.verifyAndStoreToken(jwtToken, idToken, props);
      } catch (err) {
        logger.error(`Failed login internal(${(err as Error).message}`, err);
        throw err;
      }
    },
    [authEvents, logger],
  );

  /**
   * !! This method should only be called, when the auth state is set to SignedOff !!
   *
   * Logs out the user
   *  - from SSO
   *  - across the browser tabs
   *  - cleans up the local application state
   *
   * @param ssoLogout We need the OIDC logout method to have as a parameter because we cannot use the hook, because there would be a
   * circular dependency.
   * @param reason set this reason if you want to log out because of a specific reason which needed to be displayed on the login screen
   */
  const initiateLogout = useCallback(
    async (ssoLogout: () => Promise<void>, reason: LogoutReason | undefined) => {
      logger.info(`Initiate Logout (reason=${reason || '-'})`);
      if (logoutInProgress.current) {
        return;
      }
      logoutInProgress.current = true;

      try {
        sseManager.broadcastLogout();
      } catch (broadcastErr) {
        logger.error(`Failed to broadcast logout`, broadcastErr);
      }

      try {
        await authEvents.handleSignOff(ssoLogout, reason);
      } catch (logoutErr) {
        logger.error(`Failed to logout`, logoutErr);
      } finally {
        logoutInProgress.current = false;
      }
    },
    [authEvents, logger],
  );

  const loadSSEManager = useCallback(() => {
    setAppAuth({ isAuthenticated: false, authStatus: AuthState.Unknown, stage: UnknownAppStage.InitSSEManager });
  }, [setAppAuth]);

  const loadNextAppStage = useCallback(async () => {
    if (!appAuth.isAuthenticated || appAuth.authStatus !== AuthState.Loading) {
      throw new Error(`Can't initialize app, because user is not authenticated`);
    }
    if (appAuth.stage === AuthStage.OtherInstanceLoginInProgress) {
      // when another instance is currently logging in, we don't want to start the next stage, just wait until it finishes
      return;
    }
    if (initInProgress.current) {
      // init is already started
      return;
    }

    initInProgress.current = true;

    try {
      const currentStepIndex = AppAuthSteps.indexOf(appAuth.stage);
      if (currentStepIndex === -1) {
        throw new Error(`Auth stage is not found in steps: ${appAuth.stage}`);
      }
      const currentStep = AppAuthSteps[currentStepIndex];
      const nextStep = AppAuthSteps[currentStepIndex + 1];
      const canContinue = await AppAuthStepFunctions[currentStep]();

      if (canContinue !== false) {
        if (!nextStep) {
          setAppAuth({ ...appAuth, authStatus: AuthState.Authenticated });
        } else {
          setAppAuth({ ...appAuth, stage: nextStep });
        }
      }
    } catch (err) {
      const reason =
        isAxiosError(err) && err.response?.status === 401
          ? LogoutReason.TokenExpired
          : LogoutReason.SignInFailedToInitializeApp;
      logger.error(
        `Error while completing app loading (reason=${reason})`,
        isAxiosError(err) ? { response: err.response?.data, headers: err.config?.headers?.toJSON(true) } : err,
      );
      logout(reason);
    } finally {
      initInProgress.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- no need for AppAuthStepFunctions here
  }, [appAuth, setAppAuth, logger, logout]);

  return useMemo(
    () => ({
      loginInternal,
      initiateLogout,
      loadNextAppStage,
      loadSSEManager,
      appAuth,
    }),
    [loginInternal, initiateLogout, appAuth, loadNextAppStage, loadSSEManager],
  );
};
